import { z } from 'zod';
import { OrderStatusSchema } from './orders';
import { VehicleClassSchema } from './riders';

/* ------------------------------------------------------------------ */
/* Tracking + ETA + delivery proof (W14)                               */
/*                                                                     */
/* Rider GPS (batched, mock-GPS checked), live updates over Socket.io  */
/* (/v1/live, polling fallback with the same OrderTrack payload), ETA  */
/* ranges with a silent late credit, the delivery code / location gate */
/* / leave-at-door photo / tamper seal, and the masked-call + chat     */
/* seams. Customers never see raw GPS history, rider phone numbers or  */
/* the rider's position before pickup.                                 */
/* ------------------------------------------------------------------ */

const Lat = z.number().min(-90).max(90);
const Lng = z.number().min(-180).max(180);
const Reason = z.string().trim().min(3, 'Say why (at least 3 characters)').max(300);

/** Socket.io path on the API server. */
export const LIVE_PATH = '/v1/live';
/** The app switches to polling GET …/track after the socket has been down this long. */
export const LIVE_FALLBACK_AFTER_SECONDS = 10;
/** Polling interval for GET /v1/customer/me/orders/:id/track. */
export const TRACK_POLL_SECONDS = 15;
/** Most GPS points one batch may carry (the app queues points while offline). */
export const MAX_LOCATION_BATCH = 30;
/** Wrong delivery-code tries before the code locks and Ops must override. */
export const DELIVERY_CODE_MAX_TRIES = 5;
/** Leave-at-door photos are kept for this many days. */
export const PROOF_PHOTO_DAYS = 30;
/** The location gate ignores fixes less accurate than this (metres). */
export const DELIVERY_GPS_MAX_ACCURACY_M = 100;
/** Biggest proof photo accepted (bytes). */
export const PROOF_PHOTO_MAX_BYTES = 8 * 1024 * 1024;
export const PROOF_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

/* ---------------- rider location ---------------- */

export const GpsPointSchema = z.object({
  lat: Lat,
  lng: Lng,
  /** Horizontal accuracy reported by the phone (metres). */
  accuracyM: z.number().min(0).max(10_000),
  speedMps: z.number().min(0).max(200).nullable().optional(),
  headingDeg: z.number().min(0).max(360).nullable().optional(),
  /** When the fix was taken (ISO date-time, from the phone). */
  at: z.iso.datetime({ offset: true }),
  /** The OS says this fix came from a mock-location provider. */
  mocked: z.boolean().optional(),
});
export type GpsPoint = z.infer<typeof GpsPointSchema>;

export const LocationBatchInputSchema = z.object({
  points: z.array(GpsPointSchema).min(1).max(MAX_LOCATION_BATCH),
});
export type LocationBatchInput = z.infer<typeof LocationBatchInputSchema>;

export const GPS_FLAG_KINDS = ['os_mocked', 'too_fast', 'zero_accuracy', 'teleport'] as const;
export const GpsFlagKindSchema = z.enum(GPS_FLAG_KINDS);
export type GpsFlagKind = z.infer<typeof GpsFlagKindSchema>;
export const GPS_FLAG_LABELS: Record<GpsFlagKind, string> = {
  os_mocked: 'Phone reported a fake location',
  too_fast: 'Moving faster than possible',
  zero_accuracy: 'Accuracy reported as exactly 0 again and again',
  teleport: 'Jumped more than 2 km in under 30 s',
};

export const LocationBatchResultSchema = z.object({
  accepted: z.number().int(),
  /** Points flagged by the mock-GPS check in this batch. */
  flagged: z.number().int(),
  /** The open trip the points were stored against (null when none). */
  tripId: z.uuid().nullable(),
  /** The rider is now within tracking.nearby_m of the customer (after pickup). */
  nearby: z.boolean(),
  /** Shown as a banner in the Rider app ("Turn off fake location apps"). */
  warning: z.string().nullable(),
  /** How often to send points now (seconds): fast while on a trip, slow otherwise. */
  nextIntervalSeconds: z.number().int(),
});
export type LocationBatchResult = z.infer<typeof LocationBatchResultSchema>;

/* ---------------- customer track ---------------- */

/** The customer-facing steps (W14 §4). */
export const TRACK_STAGES = [
  'placed',
  'accepted',
  'preparing',
  'ready',
  'rider_assigned',
  'picked_up',
  'nearby',
  'delivered',
  'cancelled',
] as const;
export const TrackStageSchema = z.enum(TRACK_STAGES);
export type TrackStage = z.infer<typeof TrackStageSchema>;
export const TRACK_STAGE_LABELS: Record<TrackStage, string> = {
  placed: 'Order placed',
  accepted: 'Restaurant accepted',
  preparing: 'Preparing your food',
  ready: 'Food is ready',
  rider_assigned: 'Rider on the way to the restaurant',
  picked_up: 'Picked up — on the way',
  nearby: 'Rider is nearby',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

export const TrackStepSchema = z.object({
  stage: TrackStageSchema,
  label: z.string(),
  at: z.string().nullable(),
  done: z.boolean(),
});
export type TrackStep = z.infer<typeof TrackStepSchema>;

export const ETA_MODES = ['range', 'countdown', 'none'] as const;
export const EtaModeSchema = z.enum(ETA_MODES);
export type EtaMode = z.infer<typeof EtaModeSchema>;

export const EtaViewSchema = z.object({
  /** range before pickup, a single countdown after pickup, none once finished. */
  mode: EtaModeSchema,
  fromAt: z.string().nullable(),
  toAt: z.string().nullable(),
  /** Minutes from now (rounded) for the shown range / countdown. */
  fromMinutes: z.number().int().nullable(),
  toMinutes: z.number().int().nullable(),
  computedAt: z.string().nullable(),
});
export type EtaView = z.infer<typeof EtaViewSchema>;

export const TrackPointSchema = z.object({
  lat: z.number(),
  lng: z.number(),
  headingDeg: z.number().nullable(),
  at: z.string(),
});
export type TrackPoint = z.infer<typeof TrackPointSchema>;

/** What the customer sees of the rider (never a phone number). */
export const TrackRiderSchema = z.object({
  name: z.string(),
  photoUrl: z.string().nullable(),
  rating: z.number().nullable(),
  vehicleClass: VehicleClassSchema.nullable(),
  /** Last 4 characters of the number plate, when there is one. */
  vehicleTail: z.string().nullable(),
});
export type TrackRider = z.infer<typeof TrackRiderSchema>;

export const OrderTrackSchema = z.object({
  orderId: z.uuid(),
  orderNumber: z.string(),
  status: OrderStatusSchema,
  stage: TrackStageSchema,
  steps: z.array(TrackStepSchema),
  eta: EtaViewSchema,
  rider: TrackRiderSchema.nullable(),
  /** Rider assigned but not picked up yet: "on the way to the restaurant" (no coordinates). */
  riderToRestaurant: z.boolean(),
  /** Latest rider position — only after pickup and until delivered. */
  riderLocation: TrackPointSchema.nullable(),
  restaurant: z.object({
    name: z.string(),
    lat: z.number().nullable(),
    lng: z.number().nullable(),
  }),
  drop: z.object({ lat: z.number().nullable(), lng: z.number().nullable() }),
  nearby: z.boolean(),
  deliveryCodeRequired: z.boolean(),
  leaveAtDoor: z.boolean(),
  /** Set when the order came late and a credit was given ("Sorry it was late — …"). */
  lateApology: z.string().nullable(),
  /** Leave-at-door proof photo (signed link, 30 days). */
  proofPhotoUrl: z.string().nullable(),
  /** Customer ↔ rider chat is open (a rider is on the trip and it is not finished). */
  chatOpen: z.boolean(),
  pollSeconds: z.number().int(),
  updatedAt: z.string(),
});
export type OrderTrack = z.infer<typeof OrderTrackSchema>;

export const DeliveryCodeViewSchema = z.object({
  required: z.boolean(),
  /** The 4-digit code (only for the order's owner, only while it is still needed). */
  code: z.string().nullable(),
  locked: z.boolean(),
  /** Shown to the receiver: "Share this code with the rider only at your door." */
  hint: z.string(),
});
export type DeliveryCodeView = z.infer<typeof DeliveryCodeViewSchema>;

/* ---------------- live events (Socket.io) ---------------- */

export const LIVE_EVENTS = {
  orderStatus: 'order.status',
  riderLocation: 'order.rider_location',
  orderMessage: 'order.message',
  opsRiders: 'ops.riders',
  riderOffer: 'rider.offer',
  riderOfferClosed: 'rider.offer_closed',
  /** Ops unlocked a delivery gate for the rider's trip (payload: TripGateState). */
  riderOverride: 'rider.override',
  /** W18: a new inbox message for the signed-in person (payload: InboxLiveEvent). */
  inboxNew: 'inbox.new',
} as const;
export type LiveEventName = (typeof LIVE_EVENTS)[keyof typeof LIVE_EVENTS];

/** Client → server: join or leave a room. The ack carries { ok, error? }. */
export const LIVE_CLIENT_EVENTS = { subscribe: 'subscribe', unsubscribe: 'unsubscribe' } as const;

export const LiveRoomSchema = z
  .string()
  .regex(
    /^(order:[0-9a-f-]{36}|ops:town:[0-9a-f-]{36}|rider:[0-9a-f-]{36}|ticket:[0-9a-f-]{36}|support:queue:(en|hi|ne|all))$/,
    'Unknown room',
  );
export const LiveSubscribeInputSchema = z.object({ room: LiveRoomSchema });
export type LiveSubscribeInput = z.infer<typeof LiveSubscribeInputSchema>;
export const LiveAckSchema = z.object({ ok: z.boolean(), error: z.string().optional() });
export type LiveAck = z.infer<typeof LiveAckSchema>;

export const liveRooms = {
  order: (orderId: string) => `order:${orderId}`,
  opsTown: (townId: string) => `ops:town:${townId}`,
  rider: (riderId: string) => `rider:${riderId}`,
};

/** order.status — the same payload as GET …/track. */
export type OrderStatusEvent = OrderTrack;

export const RiderLocationEventSchema = z.object({
  orderId: z.uuid(),
  /** Rounded to 5 decimals (≈ 1 m). */
  lat: z.number(),
  lng: z.number(),
  headingDeg: z.number().nullable(),
  at: z.string(),
  nearby: z.boolean(),
});
export type RiderLocationEvent = z.infer<typeof RiderLocationEventSchema>;

export const RiderOfferEventSchema = z.object({
  tripId: z.uuid(),
  orderId: z.uuid(),
  orderNumber: z.string().optional(),
  restaurantName: z.string().optional(),
  expiresAt: z.string().optional(),
});
export type RiderOfferEvent = z.infer<typeof RiderOfferEventSchema>;

export const RiderOfferClosedEventSchema = z.object({
  tripId: z.uuid(),
  orderId: z.uuid().optional(),
  reason: z.string().optional(),
});
export type RiderOfferClosedEvent = z.infer<typeof RiderOfferClosedEventSchema>;

/* ---------------- Ops live map ---------------- */

export const OPS_RIDER_STATES = [
  'idle',
  'offered',
  'to_restaurant',
  'at_restaurant',
  'delivering',
  'offline',
] as const;
export const OpsRiderStateSchema = z.enum(OPS_RIDER_STATES);
export type OpsRiderState = z.infer<typeof OpsRiderStateSchema>;

export const OpsLiveRiderSchema = z.object({
  riderId: z.uuid(),
  name: z.string(),
  state: OpsRiderStateSchema,
  vehicleClass: VehicleClassSchema.nullable(),
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  headingDeg: z.number().nullable(),
  at: z.string().nullable(),
  tripId: z.uuid().nullable(),
  orderId: z.uuid().nullable(),
  gpsSuspect: z.boolean(),
});
export type OpsLiveRider = z.infer<typeof OpsLiveRiderSchema>;

export const OpsLiveOrderSchema = z.object({
  orderId: z.uuid(),
  orderNumber: z.string(),
  status: OrderStatusSchema,
  stage: TrackStageSchema,
  restaurant: z.object({
    name: z.string(),
    lat: z.number().nullable(),
    lng: z.number().nullable(),
  }),
  drop: z.object({ lat: z.number().nullable(), lng: z.number().nullable() }),
  riderId: z.uuid().nullable(),
  etaToAt: z.string().nullable(),
  promisedToAt: z.string().nullable(),
  late: z.boolean(),
  lastPoint: TrackPointSchema.nullable(),
});
export type OpsLiveOrder = z.infer<typeof OpsLiveOrderSchema>;

export const OpsLiveQuerySchema = z.object({ townId: z.uuid() });
export type OpsLiveQuery = z.infer<typeof OpsLiveQuerySchema>;

export const OpsLiveMapSchema = z.object({
  townId: z.uuid(),
  riders: z.array(OpsLiveRiderSchema),
  orders: z.array(OpsLiveOrderSchema),
  at: z.string(),
});
export type OpsLiveMap = z.infer<typeof OpsLiveMapSchema>;

/** ops.riders — batched every 5 s per town. */
export const OpsRidersEventSchema = z.object({
  townId: z.uuid(),
  riders: z.array(OpsLiveRiderSchema),
  at: z.string(),
});
export type OpsRidersEvent = z.infer<typeof OpsRidersEventSchema>;

export const GPS_FLAG_STATUSES = ['open', 'confirmed', 'dismissed'] as const;
export const GpsFlagStatusSchema = z.enum(GPS_FLAG_STATUSES);
export type GpsFlagStatus = z.infer<typeof GpsFlagStatusSchema>;

export const GpsFlagSchema = z.object({
  id: z.uuid(),
  riderId: z.uuid(),
  riderName: z.string(),
  tripId: z.uuid().nullable(),
  orderId: z.uuid().nullable(),
  kind: GpsFlagKindSchema,
  label: z.string(),
  detail: z.record(z.string(), z.unknown()),
  at: z.string(),
  status: GpsFlagStatusSchema,
  decidedBy: z.string().nullable(),
  decidedAt: z.string().nullable(),
  note: z.string().nullable(),
});
export type GpsFlag = z.infer<typeof GpsFlagSchema>;

export const GpsFlagDecisionInputSchema = z.object({
  note: z.string().trim().max(300).nullable().optional(),
});
export type GpsFlagDecisionInput = z.infer<typeof GpsFlagDecisionInputSchema>;

export const GpsFlagDecisionSchema = z.object({
  flag: GpsFlagSchema,
  /** Confirming suspends the rider for riders.fake_gps_suspension_hours. */
  suspendedUntil: z.string().nullable(),
});
export type GpsFlagDecision = z.infer<typeof GpsFlagDecisionSchema>;

/* ---------------- delivery proof ---------------- */

export const PROOF_METHODS = ['code', 'slide', 'photo', 'override'] as const;
export const ProofMethodSchema = z.enum(PROOF_METHODS);
export type ProofMethod = z.infer<typeof ProofMethodSchema>;

export const SEAL_RESULTS = ['match', 'mismatch', 'missing', 'not_recorded'] as const;
export const SealResultSchema = z.enum(SEAL_RESULTS);
export type SealResult = z.infer<typeof SealResultSchema>;

export const DeliveryProofSchema = z.object({
  method: ProofMethodSchema,
  codeVerified: z.boolean(),
  distanceM: z.number().nullable(),
  gpsAccuracyM: z.number().nullable(),
  photoUrl: z.string().nullable(),
  sealResult: SealResultSchema.nullable(),
  overrideReason: z.string().nullable(),
  overriddenBy: z.string().nullable(),
  at: z.string(),
});
export type DeliveryProof = z.infer<typeof DeliveryProofSchema>;

/** Why Deliver did not unlock (returned in error.details.reason). */
export const DELIVERY_GATE_REASONS = [
  'code_required',
  'code_wrong',
  'code_locked',
  'too_far',
  'gps_unavailable',
  'photo_required',
  'seal_mismatch',
] as const;
export const DeliveryGateReasonSchema = z.enum(DELIVERY_GATE_REASONS);
export type DeliveryGateReason = z.infer<typeof DeliveryGateReasonSchema>;
export const DELIVERY_GATE_MESSAGES: Record<DeliveryGateReason, string> = {
  code_required: 'Ask the customer for the 4-digit delivery code.',
  code_wrong: 'That code is not right. Check it with the customer.',
  code_locked: 'Too many wrong codes. Call Capsico Ops to finish this delivery.',
  too_far: 'You are not at the drop location yet. Move closer to unlock Deliver.',
  gps_unavailable: 'Your location is not accurate enough. Step outside or ask Ops for an override.',
  photo_required: 'Take a photo of the order at the door first.',
  seal_mismatch: 'Ask the kitchen to reseal the bag.',
};

/** The tamper-seal check at pickup (scan or type the code, or report it missing). */
export const SealCheckInputSchema = z
  .object({
    code: z.string().trim().toUpperCase().min(4).max(20).nullable().optional(),
    missing: z.boolean().optional(),
    reason: z.string().trim().max(300).nullable().optional(),
  })
  .refine((v) => Boolean(v.code) !== Boolean(v.missing), {
    message: 'Scan the seal code, or say the seal is missing',
  })
  .refine((v) => !v.missing || (v.reason ?? '').trim().length >= 3, {
    message: 'Say why the seal is missing',
    path: ['reason'],
  });
export type SealCheckInput = z.infer<typeof SealCheckInputSchema>;

export const SealCheckResultSchema = z.object({
  tripId: z.uuid(),
  sealResult: SealResultSchema,
  /** Pickup is allowed with this result. */
  canPickUp: z.boolean(),
  message: z.string(),
});
export type SealCheckResult = z.infer<typeof SealCheckResultSchema>;

export const ProofPhotoStartInputSchema = z.object({
  contentType: z.enum(PROOF_PHOTO_TYPES),
  sizeBytes: z.number().int().min(1).max(PROOF_PHOTO_MAX_BYTES, 'Photos can be at most 8 MB'),
});
export type ProofPhotoStartInput = z.infer<typeof ProofPhotoStartInputSchema>;

export const ProofPhotoTicketSchema = z.object({
  uploadId: z.uuid(),
  /** Pass this as photoKey to POST …/delivered after completing the upload. */
  photoKey: z.string(),
  url: z.string(),
  method: z.literal('PUT'),
  headers: z.record(z.string(), z.string()),
  expiresAt: z.string(),
});
export type ProofPhotoTicket = z.infer<typeof ProofPhotoTicketSchema>;

export const ProofPhotoViewSchema = z.object({
  uploadId: z.uuid(),
  photoKey: z.string(),
  uploaded: z.boolean(),
});
export type ProofPhotoView = z.infer<typeof ProofPhotoViewSchema>;

export const OVERRIDE_KINDS = ['code', 'location'] as const;
export const OverrideKindSchema = z.enum(OVERRIDE_KINDS);
export type OverrideKind = z.infer<typeof OverrideKindSchema>;

/** Rider asks Ops to unlock the code or the location gate (e.g. no GPS). */
export const OverrideRequestInputSchema = z.object({
  kind: OverrideKindSchema,
  reason: Reason,
});
export type OverrideRequestInput = z.infer<typeof OverrideRequestInputSchema>;

/** Ops unlocks the gate for one trip, with a typed reason. */
export const OpsOverrideInputSchema = z.object({
  kind: OverrideKindSchema,
  reason: Reason,
});
export type OpsOverrideInput = z.infer<typeof OpsOverrideInputSchema>;

export const TripGateStateSchema = z.object({
  tripId: z.uuid(),
  orderId: z.uuid(),
  codeRequired: z.boolean(),
  codeVerified: z.boolean(),
  codeLocked: z.boolean(),
  codeOverridden: z.boolean(),
  locationOverridden: z.boolean(),
  leaveAtDoor: z.boolean(),
  sealResult: SealResultSchema.nullable(),
  overrideRequested: OverrideKindSchema.nullable(),
  overrideRequestNote: z.string().nullable(),
  gpsSuspect: z.boolean(),
});
export type TripGateState = z.infer<typeof TripGateStateSchema>;

/* ---------------- Ops order path ---------------- */

export const PathPointSchema = TrackPointSchema.extend({
  accuracyM: z.number(),
  mocked: z.boolean(),
  tripId: z.uuid().nullable(),
});
export type PathPoint = z.infer<typeof PathPointSchema>;

export const OpsOrderTrackingSchema = z.object({
  orderId: z.uuid(),
  orderNumber: z.string(),
  track: OrderTrackSchema,
  points: z.array(PathPointSchema),
  proof: DeliveryProofSchema.nullable(),
  flags: z.array(GpsFlagSchema),
  gates: TripGateStateSchema.nullable(),
  etaPromisedToAt: z.string().nullable(),
  lateCreditAt: z.string().nullable(),
});
export type OpsOrderTracking = z.infer<typeof OpsOrderTrackingSchema>;

export const GpsFlagListQuerySchema = z.object({
  status: GpsFlagStatusSchema.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type GpsFlagListQuery = z.infer<typeof GpsFlagListQuerySchema>;

export const GpsFlagListSchema = z.object({ rows: z.array(GpsFlagSchema) });
export type GpsFlagList = z.infer<typeof GpsFlagListSchema>;

/* ---------------- masked calls (seam) ---------------- */

export const CALL_PARTIES = ['customer', 'rider', 'restaurant', 'support'] as const;
export const CallPartySchema = z.enum(CALL_PARTIES);
export type CallParty = z.infer<typeof CallPartySchema>;

export const CallBridgeInputSchema = z.object({
  orderId: z.uuid(),
  to: CallPartySchema,
});
export type CallBridgeInput = z.infer<typeof CallBridgeInputSchema>;

export const CALL_BRIDGE_PROVIDERS = ['none', 'mock'] as const;
export type CallBridgeProviderName = (typeof CALL_BRIDGE_PROVIDERS)[number];

export const CallBridgeResultSchema = z.object({
  /** The number to dial (a masked bridge number, or the Capsico support line). */
  dialNumber: z.string().nullable(),
  expiresAt: z.string().nullable(),
  provider: z.string(),
  /** Shown when there is no number ("Use chat or call Capsico support"). */
  message: z.string().nullable(),
});
export type CallBridgeResult = z.infer<typeof CallBridgeResultSchema>;

/* ---------------- chat (seam) ---------------- */

export const MESSAGE_SENDERS = ['customer', 'rider'] as const;
export const MessageSenderSchema = z.enum(MESSAGE_SENDERS);
export type MessageSender = z.infer<typeof MessageSenderSchema>;

export const QUICK_REPLIES = {
  at_gate: "I'm at the gate",
  please_call: 'Please call me',
  coming_down: 'Coming down',
  wait_two: 'Please wait 2 minutes',
  on_the_way: 'On my way',
  reached: 'I have reached',
} as const;
export type QuickReply = keyof typeof QUICK_REPLIES;
export const QuickReplySchema = z.enum(Object.keys(QUICK_REPLIES) as [QuickReply, ...QuickReply[]]);

export const OrderMessageSchema = z.object({
  id: z.uuid(),
  sender: MessageSenderSchema,
  body: z.string(),
  quickReply: QuickReplySchema.nullable(),
  at: z.string(),
  mine: z.boolean(),
});
export type OrderMessage = z.infer<typeof OrderMessageSchema>;

export const PostOrderMessageInputSchema = z
  .object({
    body: z.string().trim().min(1).max(500).optional(),
    quickReply: QuickReplySchema.optional(),
  })
  .refine((v) => Boolean(v.body) || Boolean(v.quickReply), {
    message: 'Type a message or pick a quick reply',
  });
export type PostOrderMessageInput = z.infer<typeof PostOrderMessageInputSchema>;

export const OrderMessageListSchema = z.object({
  orderId: z.uuid(),
  open: z.boolean(),
  messages: z.array(OrderMessageSchema),
  quickReplies: z.array(z.object({ key: QuickReplySchema, text: z.string() })),
});
export type OrderMessageList = z.infer<typeof OrderMessageListSchema>;

/* ---------------- daily job ---------------- */

export const TrackingDailyResultSchema = z.object({
  pointsPurged: z.number().int(),
  photosPurged: z.number().int(),
  messagesKept: z.number().int(),
  suspensionsLifted: z.number().int(),
});
export type TrackingDailyResult = z.infer<typeof TrackingDailyResultSchema>;

export const ProofPhotoParamsSchema = z.object({
  id: z.uuid(),
  uploadId: z.uuid(),
});
export const LatLngSchema = z.object({ lat: Lat, lng: Lng });
